import { ScrollArea } from "@base-ui/react/scroll-area"
import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { OverlayStyle } from "shared/context/overlay/types"
import { IconSize } from "shared/types/Icon"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { OverlayWrapper } from "shared/ui/overlay/OverlayWrapper"

import "./styles/_actions-overlay.scss"
import { ActionsOverlayViewModel } from "./types/ActionOverlayViewModel"
import { ActionsOverlayActionType } from "./types/ActionsOverlayAction"

export const ActionsOverlayView = React.memo(
  ({ id, actions, headline, className, onCancel }: ActionsOverlayViewModel) => {
    const { t } = useTranslation()

    return (
      <OverlayWrapper
        id={id}
        headline={headline}
        overlayStyle={OverlayStyle.ACTIONS}
        className={className}
      >
        <ScrollArea.Root className="scroll-area scroll-area--vertical actions-overlay__scroll-area">
          <ScrollArea.Viewport className="scroll-area__viewport">
            <ScrollArea.Content className="scroll-area__content">
              <div
                role="list"
                className={classNames("actions-overlay__actions")}
              >
                {actions.map((action, index) => {
                  const { label, icon, ...restAction } = action

                  return (
                    <div
                      role="listitem"
                      className={classNames(
                        "actions-overlay__action",
                        {
                          "actions-overlay__action--first": index === 0,
                          "actions-overlay__action--last":
                            index === actions.length - 1
                        },
                        `actions-overlay__action--${action.actionType.toLowerCase()}`
                      )}
                      key={action.id}
                    >
                      <Button
                        variant={ButtonVariant.TERTIARY}
                        iconSize={IconSize.SMALL}
                        data-testid={action.id}
                        label={label}
                        {...restAction}
                      >
                        <div className="actions-overlay__action-icon">
                          {icon ? <Icon name={icon} /> : null}
                        </div>
                        <div className="actions-overlay__action-label">
                          {label}
                        </div>
                        <div className="actions-overlay__action-arrow">
                          {action.actionType ===
                          ActionsOverlayActionType.GROUP ? (
                            <Icon name={IconName.ARROW_RIGHT} />
                          ) : null}
                          {action.actionType ===
                          ActionsOverlayActionType.BACK ? (
                            <Icon name={IconName.ARROW_LEFT} />
                          ) : null}
                        </div>
                      </Button>
                    </div>
                  )
                })}
              </div>
            </ScrollArea.Content>
          </ScrollArea.Viewport>
          <ScrollArea.Scrollbar
            orientation="vertical"
            className="scroll-area__scrollbar"
          >
            <ScrollArea.Thumb className="scroll-area__thumb" />
          </ScrollArea.Scrollbar>
        </ScrollArea.Root>
        <div className="actions-overlay__horizontal-space" />
        <div className="actions-overlay__action actions-overlay__action--cancel">
          <Button
            variant={ButtonVariant.TERTIARY}
            id="actions-overlay__cancel"
            onClick={onCancel}
            data-testid="actions-overlay__cancel"
          >
            {t("General.Cancel")}
          </Button>
        </div>
      </OverlayWrapper>
    )
  }
)
